import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DragOverlay from '@components/DragOverlay/DragOverlay';
import IconButton, { ICON_BUTTON_VARIANT_PRIMARY, ICON_BUTTON_VARIANT_TONAL } from '@components/1-atoms/IconButton';
import { AttachmentTray, fromFiles } from '@components/Attachments';
import {
  InputSection,
  InputSectionContent,
  InputContainer,
  InputWrapper,
  Input,
  HiddenFileInput,
  AttachmentPreviewArea,
  CharacterCount,
  DisclaimerText,
  FileErrorText,
  RunnerPickerRow,
} from './Chat.styled';
import RunnerPicker from './RunnerPicker';
import ApprovalModePicker from './ApprovalModePicker';
import { listLocalRunners } from '@api/localRunners';
import type { LocalRunner } from '@api/localRunners';
import { useChatInputLimits } from '@hooks/useChatInputLimits';
import { useFileAttachments } from '@hooks/useFileAttachments';
import { useFileDragDrop } from '@hooks/useFileDragDrop';
import LlmModelPicker from './LlmModelPicker';
import { collectClipboardImages } from '@utils/clipboardFiles';
import { COUNTER_VISIBLE_RATIO } from '@components/PromptComposer/PromptComposer.constants';

export type { ApprovalMode } from '@utils/approvalMode';
import { type ApprovalMode, APPROVAL_MODE_DEFAULT, getStoredApprovalMode, setStoredApprovalMode } from '@utils/approvalMode';
import { getStoredLlmModel, setStoredLlmModel, LLM_MODEL_CHANGED_EVENT, type LlmModelChangedDetail } from '@utils/llmModel';
import type { AskUserAnswer, PendingAskUser } from '@hooks/useChat';
import ClarificationCard from './ClarificationCard';
import { Dock, EscapeHint, CardIcon } from './ClarificationCard.styled';

/** Text put in the composer from outside; a new nonce puts it there again. */
export interface ChatDraft {
  text: string;
  nonce: number;
  /** Send it at once instead, unless a turn is running or a question waits for an answer. */
  send?: boolean;
}

interface ChatInputAreaProps {
  sendMessage: (message: string, files?: File[], mode?: string, runnerType?: string, approvalMode?: string) => Promise<void | boolean>;
  isLoading: boolean;
  /** Disable all inputs without showing the stop button (e.g. while reconnecting). */
  isInputDisabled?: boolean;
  onCancel?: () => void;
  /** Pre-select a runner (e.g. from the homepage picker). Defaults to 'cloud'. */
  initialRunnerType?: string;
  /** Conversation ID for persisting runner selection */
  conversationId?: string | null;
  /** Callback when runner type changes */
  onRunnerTypeChange?: (runnerType: string) => void;
  /** Callback when approval mode changes */
  onApprovalModeChange?: (approvalMode: string) => void;
  /** Pending clarification batch awaiting answers — rendered as a card above the composer (AIB-6745) */
  pendingAskUser?: PendingAskUser | null;
  /** Submit clarification answers via the card */
  onSubmitAskUser?: (answers: AskUserAnswer[], crops?: File[], runnerType?: string, approvalMode?: string) => void | Promise<void>;
  /** User typed a reply instead of using the card — skip with the option legend */
  onSkipAskUser?: (message: string, files?: File[], runnerType?: string, approvalMode?: string) => void | Promise<void>;
  /**
   * Hide the runner / approval-mode / model row. For surfaces where the turn
   * never reaches a runner and the model is fixed server-side, so the pickers
   * would only offer choices that do nothing (the process assistant panel).
   */
  hideExecutionControls?: boolean;
  /**
   * Overrides the composer placeholder. The default one wraps in a panel as
   * narrow as the process assistant's, leaving only its tail on screen.
   */
  inputPlaceholder?: string;
  /** Replaces what is in the composer, for the user to review and send. */
  draft?: ChatDraft;
}

const RUNNER_STORAGE_KEY = 'aibuild_runner_selection';
const RUNNER_DEFAULT_KEY = 'aibuild_runner_selection_default';

const getStoredRunner = (conversationId: string | null): string | null => {
  if (conversationId) {
    try {
      const stored = localStorage.getItem(RUNNER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed[conversationId]) return parsed[conversationId];
      }
    } catch {
      // Fall through to global default
    }
  }
  // Fall back to global default (the last runner selected in any conversation)
  try {
    return localStorage.getItem(RUNNER_DEFAULT_KEY) || null;
  } catch {
    return null;
  }
};

const setStoredRunner = (conversationId: string | null, runnerType: string): void => {
  // Always update the global default so new conversations inherit the last choice
  try {
    localStorage.setItem(RUNNER_DEFAULT_KEY, runnerType);
  } catch {
    // Ignore storage errors
  }
  if (!conversationId) return;
  try {
    const stored = localStorage.getItem(RUNNER_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[conversationId] = runnerType;
    localStorage.setItem(RUNNER_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // Ignore storage errors
  }
};

const ChatInputArea: React.FC<ChatInputAreaProps> = ({ sendMessage, isLoading, isInputDisabled = false, onCancel, initialRunnerType, conversationId, onRunnerTypeChange, onApprovalModeChange, pendingAskUser, onSubmitAskUser, onSkipAskUser, hideExecutionControls = false, inputPlaceholder, draft }) => {
  const inputsDisabled = isLoading || isInputDisabled;
  const [inputValue, setInputValue] = useState('');
  const [draftNonce, setDraftNonce] = useState<number | null>(null);
  const [draftToSend, setDraftToSend] = useState<ChatDraft | null>(null);
  if (draft && draft.nonce !== draftNonce) {
    setDraftNonce(draft.nonce);
    if (draft.send && !inputsDisabled && !pendingAskUser) setDraftToSend(draft);
    else setInputValue(draft.text);
  }
  const [fileInputKey, setFileInputKey] = useState(0);
  const { maxAttachmentsPerMessage, maxMessageLengthChars } = useChatInputLimits();
  const { pendingFiles, fileError, remainingSlots, handleFileSelect, handleRemoveFile, clearFiles } =
    useFileAttachments({ maxFiles: maxAttachmentsPerMessage });
  const pendingItems = useMemo(() => fromFiles(pendingFiles), [pendingFiles]);
  const { isDragOver } = useFileDragDrop(handleFileSelect);
  const [localRunners, setLocalRunners] = useState<LocalRunner[]>([]);
  const [runnersLoaded, setRunnersLoaded] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState<number | undefined>(undefined);
  const [textareaScrollable, setTextareaScrollable] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const intl = useIntl();

  useEffect(() => {
    if (draftNonce !== null) textareaRef.current?.focus();
  }, [draftNonce]);

  const derivedApprovalMode = useMemo<ApprovalMode>(() => {
    const stored = getStoredApprovalMode(conversationId ?? null);
    return stored || APPROVAL_MODE_DEFAULT;
  }, [conversationId]);

  const [approvalMode, setApprovalMode] = useState<ApprovalMode>(derivedApprovalMode);

  const derivedLlmModel = useMemo(() => getStoredLlmModel(conversationId ?? null), [conversationId]);
  const [llmModel, setLlmModel] = useState<string>(derivedLlmModel);

  useEffect(() => {
    setApprovalMode(derivedApprovalMode);
  }, [derivedApprovalMode]);

  useEffect(() => {
    setLlmModel(derivedLlmModel);
  }, [derivedLlmModel]);

  // Re-sync the picker when the model is switched programmatically (e.g. the
  // user accepts a model-swap suggestion) for this conversation.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<LlmModelChangedDetail>).detail;
      if (detail && detail.conversationId === (conversationId ?? null)) {
        setLlmModel(detail.model);
      }
    };
    globalThis.addEventListener(LLM_MODEL_CHANGED_EVENT, handler);
    return () => globalThis.removeEventListener(LLM_MODEL_CHANGED_EVENT, handler);
  }, [conversationId]);

  const handlePickApprovalMode = useCallback((mode: ApprovalMode) => {
    setApprovalMode(mode);
    setStoredApprovalMode(conversationId ?? null, mode);
  }, [conversationId]);

  const handlePickLlmModel = useCallback((model: string) => {
    setLlmModel(model);
    setStoredLlmModel(conversationId ?? null, model);
  }, [conversationId]);

  // Derive the runner type from conversationId + storage (falls back to global default, then 'cloud')
  const derivedRunnerType = useMemo(() => {
    if (initialRunnerType) return initialRunnerType;
    return getStoredRunner(conversationId ?? null) ?? 'cloud';
  }, [conversationId, initialRunnerType]);

  const [runnerType, setRunnerType] = useState<string>(derivedRunnerType);

  useEffect(() => {
    let cancelled = false;
    listLocalRunners()
      .then((runners) => {
        if (!cancelled) {
          setLocalRunners(runners);
          setRunnersLoaded(true);
        }
      })
      .catch(() => {
        // Don't set runnersLoaded on error — we can't validate runner availability
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setRunnerType(derivedRunnerType);
  }, [derivedRunnerType]);

  // Reset to 'cloud' if the stored local runner no longer exists in the fetched list.
  // Prevents sending a stale runner ID that the backend will reject.
  useEffect(() => {
    if (!runnersLoaded || runnerType === 'cloud') return;
    const stillExists = localRunners.some((r) => r.vmId === runnerType);
    if (!stillExists) {
      setRunnerType('cloud');
      setStoredRunner(conversationId ?? null, 'cloud');
    }
  }, [runnersLoaded, localRunners, runnerType, conversationId]);

  useEffect(() => {
    if (onRunnerTypeChange) {
      onRunnerTypeChange(runnerType);
    }
  }, [runnerType, onRunnerTypeChange]);

  useEffect(() => {
    if (onApprovalModeChange) {
      onApprovalModeChange(approvalMode);
    }
  }, [approvalMode, onApprovalModeChange]);

  // When conversationId transitions from null to a real ID (new conversation created),
  // persist the current runner selection and approval mode to localStorage.
  const prevConversationIdRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    // Skip the very first invocation to avoid treating an already-set conversationId
    // on mount as a null → real-ID transition, which would overwrite global defaults
    // with per-conversation storage before the user has taken any action.
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      prevConversationIdRef.current = conversationId ?? null;
      return;
    }

    const prev = prevConversationIdRef.current;
    const curr = conversationId ?? null;

    if (prev === null && curr !== null) {
      if (runnerType) {
        setStoredRunner(curr, runnerType);
      }
      setStoredApprovalMode(curr, approvalMode);
      setStoredLlmModel(curr, llmModel);
    }

    prevConversationIdRef.current = curr;
  }, [conversationId, runnerType, approvalMode, llmModel]);

  const submit = useCallback(
    async (text: string, files?: File[]): Promise<boolean> => {
      try {
        if (pendingAskUser) {
          await onSkipAskUser?.(text, files, runnerType || undefined, approvalMode);
          return true;
        }
        return (await sendMessage(text, files, undefined, runnerType || undefined, approvalMode)) !== false;
      } catch {
        // Preserve pending questions so the user can retry.
        return false;
      }
    },
    [sendMessage, runnerType, approvalMode, pendingAskUser, onSkipAskUser],
  );

  // Runs before the chat's reconnect-on-mount, which stands down once a send has started.
  const sentDraftNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!draftToSend || sentDraftNonce.current === draftToSend.nonce) return;
    sentDraftNonce.current = draftToSend.nonce;
    const { text } = draftToSend;
    submit(text)
      .then((sent) => {
        if (!sent) setInputValue(text);
      })
      .catch(() => undefined);
  }, [draftToSend, submit]);

  const handleSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if ((trimmed || pendingFiles.length > 0) && !inputsDisabled) {
        const filesToSend = pendingFiles.length > 0 ? [...pendingFiles] : undefined;
        setInputValue('');
        clearFiles();
        setTextareaHeight(undefined);
        setTextareaScrollable(false);
        if (filesToSend) {
          setFileInputKey((k) => k + 1);
        }
        await submit(trimmed || 'See attached files', filesToSend);
      }
    },
    [inputValue, pendingFiles, inputsDisabled, clearFiles, submit],
  );

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    // Briefly force auto height so scrollHeight is unconstrained, then
    // remove the inline override — the styled-component owns the actual
    // height via the $heightPx prop.
    el.style.height = 'auto';
    const scrollH = el.scrollHeight;
    el.style.height = '';
    setTextareaHeight(Math.min(scrollH, 200));
    setTextareaScrollable(scrollH > 200);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit],
  );

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imageFiles = collectClipboardImages(e.clipboardData);
    const html = e.clipboardData.getData('text/html');

    if (imageFiles.length === 0 && !html) {
      // Plain text only — let the browser handle it natively so the cursor
      // and undo stack behave as the user expects.
      return;
    }

    e.preventDefault();

    if (imageFiles.length > 0) {
      handleFileSelect(imageFiles);
    }

    // Insert any plain-text part of the clipboard at the cursor. For pure
    // image pastes this is usually empty (no-op); for rich-text or mixed
    // pastes this strips formatting while still inserting the text.
    const plain = e.clipboardData.getData('text/plain');
    const textarea = textareaRef.current;
    if (plain && textarea) {
      const { selectionStart, selectionEnd, value } = textarea;
      const head = value.substring(0, selectionStart);
      const tail = value.substring(selectionEnd);
      // Assigning value directly bypasses the textarea's own maxLength. Reapply
      // it to the pasted text alone, as a native paste does — trimming the
      // concatenation instead would silently eat text the user already typed
      // after the cursor.
      const budget = Math.max(0, maxMessageLengthChars - head.length - tail.length);
      const inserted = plain.slice(0, budget);
      setInputValue(head + inserted + tail);
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const pos = head.length + inserted.length;
          textareaRef.current.setSelectionRange(pos, pos);
        }
        autoResize();
      });
    }
  }, [autoResize, handleFileSelect, maxMessageLengthChars]);

  const handlePickRunner = useCallback((value: string) => {
    setRunnerType(value);
    setStoredRunner(conversationId ?? null, value);
  }, [conversationId]);

  return (
    <InputSection>
      <DragOverlay visible={isDragOver} />
      <InputSectionContent>
        <HiddenFileInput
          key={fileInputKey}
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => {
            handleFileSelect(e.target.files);
            e.target.value = '';
          }}
        />

        {pendingFiles.length > 0 && (
          <AttachmentPreviewArea>
            <AttachmentTray items={pendingItems} onRemove={handleRemoveFile} />
          </AttachmentPreviewArea>
        )}

        {fileError && (
          <FileErrorText role="alert" data-testid="file-error">
            {fileError}
          </FileErrorText>
        )}

        {pendingAskUser && (
          <Dock>
            <ClarificationCard
              key={pendingAskUser.askId}
              questions={pendingAskUser.questions}
              restore={pendingAskUser.restore}
              onSubmit={(answers, crops) => {
                void Promise.resolve(
                  onSubmitAskUser?.(answers, crops, runnerType || undefined, approvalMode),
                ).catch(() => undefined);
              }}
            />
            <EscapeHint>
              <CardIcon $size={14}>south</CardIcon>
              <FormattedMessage
                id="chat.clarify.escape"
                defaultMessage="or type your own reply below to skip these"
              />
            </EscapeHint>
          </Dock>
        )}
        <InputWrapper>
          <form onSubmit={handleSubmit}>
            <InputContainer>
              <IconButton
                iconName="add_0"
                variant={ICON_BUTTON_VARIANT_PRIMARY}
                dataTestId="chat-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={inputsDisabled || remainingSlots === 0}
              />
              <Input
                ref={textareaRef}
                rows={1}
                $heightPx={textareaHeight}
                $scrollable={textareaScrollable}
                placeholder={
                  inputPlaceholder ??
                  intl.formatMessage({
                    id: 'chat.input.placeholder',
                    defaultMessage: 'Assign a task or ask anything...',
                  })
                }
                value={inputValue}
                maxLength={maxMessageLengthChars}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  autoResize();
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={inputsDisabled}
                autoFocus
              />
              {isLoading && !isInputDisabled ? (
                <IconButton
                  iconName="stop_player_1"
                  variant={ICON_BUTTON_VARIANT_TONAL}
                  onClick={onCancel}
                  dataTestId="chat-cancel-btn"
                />
              ) : (
                <IconButton
                  iconName="send_0"
                  variant={ICON_BUTTON_VARIANT_TONAL}
                  submitType
                  disabled={inputsDisabled || (!inputValue.trim() && pendingFiles.length === 0)}
                  dataTestId="chat-send-btn"
                />
              )}
            </InputContainer>
          </form>
          {inputValue.length >= maxMessageLengthChars * COUNTER_VISIBLE_RATIO && (
            <CharacterCount
              $over={inputValue.length >= maxMessageLengthChars}
              data-testid="chat-char-count"
            >
              <FormattedMessage
                id="chat.input.characterCount"
                defaultMessage="{count} / {max} characters"
                values={{
                  count: inputValue.length.toLocaleString(),
                  max: maxMessageLengthChars.toLocaleString(),
                }}
              />
            </CharacterCount>
          )}
          {!hideExecutionControls && (
            <RunnerPickerRow>
              <RunnerPicker
                runnerType={runnerType}
                localRunners={localRunners}
                onPick={handlePickRunner}
                disabled={inputsDisabled}
              />
              {runnerType !== 'cloud' && (
                <ApprovalModePicker
                  approvalMode={approvalMode}
                  onPick={handlePickApprovalMode}
                  disabled={inputsDisabled}
                />
              )}
              <LlmModelPicker
                llmModel={llmModel}
                onPick={handlePickLlmModel}
                disabled={inputsDisabled}
              />
            </RunnerPickerRow>
          )}
        </InputWrapper>
        <DisclaimerText>
          <FormattedMessage
            id="chat.disclaimer.combined"
            defaultMessage="⚠️ Aibuild OS (Beta): Agents can make mistakes, and commands execute unverified with full system access. Always review outputs.{br}Feedback: support@ai-build.com."
            values={{ br: <br /> }}
          />
        </DisclaimerText>
      </InputSectionContent>
    </InputSection>
  );
};

export default React.memo(ChatInputArea);
